import { z } from "zod";
import { fileRefSchema, MAX_FILES } from "../uploads/files";

export const INCIDENT_TYPES = [
  "Customer complaint",
  "Feature / functionality issue",
  "Suspected data breach",
  "Incorrect or unintended customer communications",
  "Regulatory or statutory filing impact",
  "Platform outage / failed process",
] as const;

export const INCIDENT_SEVERITIES = ["SEV1", "SEV2", "SEV3", "SEV4"] as const;
export type IncidentSeverity = (typeof INCIDENT_SEVERITIES)[number];
export type IncidentType = (typeof INCIDENT_TYPES)[number];

export const SEVERITY_LABELS: Record<IncidentSeverity, string> = {
  SEV1: "SEV1 — Critical, major impact",
  SEV2: "SEV2 — Significant impact",
  SEV3: "SEV3 — Minor impact",
  SEV4: "SEV4 — Low impact, single customer",
};

const CHANNEL_PREFIX = "incident-";
// Slack allows 80 characters; the room left over is for a "-2" style suffix when the name is taken.
const MAX_SLUG = 80 - CHANNEL_PREFIX.length - 3;

// The title as a Slack channel name: lowercase letters, digits and single hyphens.
export function channelBaseName(title: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_SLUG)
    .replace(/-+$/g, "");
  return slug ? `${CHANNEL_PREFIX}${slug}` : "";
}

export const incidentFormSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1)
    .max(120)
    .refine((title) => channelBaseName(title) !== "", "Use some letters or numbers in the title"),
  description: z.string().trim().min(1).max(10_000),
  // An estimate, so free text ("about 10", "unknown").
  companiesImpacted: z.string().trim().min(1).max(200),
  types: z.array(z.enum(INCIDENT_TYPES)).min(1, "Select at least one type.").max(INCIDENT_TYPES.length),
  severity: z.enum(INCIDENT_SEVERITIES),
  // What the form's AI suggestion said when the person raised it, kept to see how often it was overruled. As reported by the form: it decides nothing.
  aiSeverity: z.enum(INCIDENT_SEVERITIES).optional(),
  files: z.array(fileRefSchema).max(MAX_FILES).optional(),
});
export type IncidentForm = z.infer<typeof incidentFormSchema>;

// What the owner does after the incident is raised: resolve it, confirm the type and severity, and tick off the write-up and lessons.
// The write-up itself lives elsewhere (a Google Doc); the link is optional and an empty one removes it.
const isHttpsUrl = (value: string): boolean => {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
};

export const followUpActionSchema = z.discriminatedUnion("action", [
  z.object({ action: z.literal("resolve") }),
  z.object({ action: z.literal("reopen") }),
  z.object({
    action: z.literal("confirm"),
    types: z.array(z.enum(INCIDENT_TYPES)).min(1, "Select at least one type.").max(INCIDENT_TYPES.length),
    severity: z.enum(INCIDENT_SEVERITIES),
  }),
  z.object({ action: z.literal("writeUp"), done: z.boolean() }),
  z.object({
    action: z.literal("writeUpLink"),
    url: z
      .string()
      .trim()
      .max(500, "The link is too long.")
      .refine((value) => value === "" || isHttpsUrl(value), "Enter a web address that starts with https://"),
  }),
  z.object({ action: z.literal("lessons"), done: z.boolean() }),
]);
export type FollowUpAction = z.infer<typeof followUpActionSchema>;

export const RESPONDER_ROLES = ["owner", "backup", "notify"] as const;
export type ResponderRole = (typeof RESPONDER_ROLES)[number];

export const responderSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(200),
  name: z.string().trim().min(1).max(100),
  role: z.enum(RESPONDER_ROLES),
});
export type Responder = z.infer<typeof responderSchema>;

// What an admin saves. `version` is the one they read, so two admins cannot overwrite each other.
export const responderUpdateSchema = z
  .object({
    responders: z.array(responderSchema).min(1).max(30),
    version: z.number().int().min(0),
  })
  .superRefine((update, context) => {
    const owners = update.responders.filter((responder) => responder.role === "owner").length;
    if (owners !== 1) {
      context.addIssue({ code: "custom", path: ["responders"], message: "Choose exactly one owner" });
    }
    if (!update.responders.some((responder) => responder.role === "backup")) {
      context.addIssue({ code: "custom", path: ["responders"], message: "Choose at least one backup owner" });
    }
    const emails = update.responders.map((responder) => responder.email);
    if (new Set(emails).size !== emails.length) {
      context.addIssue({ code: "custom", path: ["responders"], message: "Each person can be listed once" });
    }
  });
